import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Mail, CheckCircle, ArrowLeft, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/portal-clients/forgot-password`, {
        email,
        portal_share_token: shareToken
      });
      
      setSuccess(true);
      toast.success("If an account exists, a reset link has been sent");
    } catch (err) {
      // Always show success message to prevent email enumeration
      setSuccess(true);
      toast.success("If an account exists, a reset link has been sent");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate(`/p/${shareToken}`);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Check Your Email</h1>
          <p className="text-muted-foreground mb-6">
            If an account exists with that email address, we've sent a password reset link. Please check your inbox and spam folder.
          </p>
          <Button onClick={handleBackToLogin} className="btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card border border-border rounded-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              Forgot Password?
            </h1>
            <p className="text-muted-foreground mt-2">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-foreground/80">Email Address *</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="pl-10"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary mt-6" 
              disabled={submitting}
              data-testid="send-reset-link-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleBackToLogin}
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
